import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export const fetchServices = createAsyncThunk(
    'services/fetchServices',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getServicesPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Service Reminder');
        }
    }
);

export const getServiceOverview = createAsyncThunk(
    'services/getServiceOverview',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getServiceOverview();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Service Overview');
        }
    }
);

export const fetchVehiclesOverview = createAsyncThunk(
    'services/fetchVehiclesOverview',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehiclesOverview();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicles overview');
        }
    }
);

export const fetchVehicleData = createAsyncThunk(
    'services/fetchVehicleData',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehiclesPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicles');
        }
    }
);

export const fetchVehicleHistory = createAsyncThunk(
    'services/fetchVehicleHistory',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleHistory(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle history');
        }
    }
);

export const fetchVehicleServiceRecords = createAsyncThunk(
    'services/fetchVehicleServiceRecords',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleServices(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle service records');
        }
    }
);

export const fetchVehicleTrips = createAsyncThunk(
    'services/fetchVehicleTrips',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleTrips(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle trips');
        }
    }
);

export const fetchVehicleFuelLogs = createAsyncThunk(
    'services/fetchVehicleFuelLogs',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleFuelLogs(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle fuel logs');
        }
    }
);

export const fetchVehicleDistanceSummary = createAsyncThunk(
    'services/fetchVehicleDistanceSummary',
    async (vehicleId, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleDistanceSummary(vehicleId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle distance summary');
        }
    }
);

export const fetchVehicleInstallments = createAsyncThunk(
    'services/fetchVehicleInstallments',
    async (vehicleId, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleInstallments(vehicleId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle installments');
        }
    }
);

export const fetchVehicleAssignmentHistory = createAsyncThunk(
    'services/fetchVehicleAssignmentHistory',
    async (vehicleId, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleAssignmentHistory(vehicleId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle equipment installed history');
        }
    }
);

export const createVehicleInstallment = createAsyncThunk(
    'services/createVehicleInstallment',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.createVehicleInstallment(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create vehicle installment');
        }
    }
);

export const attachVehicleEquipment = createAsyncThunk(
    'services/attachVehicleEquipment',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.attachVehicleEquipment(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to link vehicle equipment');
        }
    }
);

export const detachVehicleEquipment = createAsyncThunk(
    'services/detachVehicleEquipment',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.detachVehicleEquipment(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to unlink vehicle equipment');
        }
    }
);

export const attachVehicleTyres = createAsyncThunk(
    'services/attachVehicleTyres',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.attachVehicleTyres(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to link vehicle tyres');
        }
    }
);

export const detachVehicleTyre = createAsyncThunk(
    'services/detachVehicleTyre',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.detachTyre(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to detach vehicle tyre');
        }
    }
);

export const updateVehicleGeneralInfo = createAsyncThunk(
    'services/updateVehicleGeneralInfo',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.updateVehicleGeneralInfo(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update vehicle general information');
        }
    }
);

export const updateVehiclePurchaseInfo = createAsyncThunk(
    'services/updateVehiclePurchaseInfo',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiService.updateVehiclePurchaseInfo(payload);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update vehicle purchase information');
        }
    }
);

export const commentOnVehicle = createAsyncThunk(
    'services/commentOnVehicle',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.commentOnVehicle(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to comment on vehicle');
        }
    }
);

export const fetchServicesReminder = createAsyncThunk(
    'services/fetchServicesReminder',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getServicesReminderPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Service Reminders');
        }
    }
);

export const deleteService = createAsyncThunk(
    'services/deleteService',
    async (serviceId, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteService(serviceId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete service');
        }
    }
);

export const deleteVehicle = createAsyncThunk(
    'services/deleteVehicle',
    async (vehicleId, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteVehicle(vehicleId);
            return { ...response, deletedId: vehicleId };
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete vehicle');
        }
    }
);

export const servicePdfExport = createAsyncThunk(
    'services/servicePdfExport',
    async (getIds, { rejectWithValue }) => {
        try {
            const response = await apiService.servicePdfExport(getIds);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to export');
        }
    }
);

export const serviceExcelExport = createAsyncThunk(
    'services/serviceExcelExport',
    async (getIds, { rejectWithValue }) => {
        try {
            const response = await apiService.serviceExcelExport(getIds);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to export');
        }
    }
);

export const getSingleService = createAsyncThunk(
    'services/getSingleService',
    async (serviceId, { rejectWithValue }) => {
        try {
            const response = await apiService.getSingleService(serviceId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch service');
        }
    }
);

export const fetchVehicleDropdown = createAsyncThunk(
    'services/fetchVehicleDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehiclesDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle dropdown');
        }
    }
);

export const fetchReasonsDropdown = createAsyncThunk(
    'services/fetchReasonsDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getReasonsDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch reasons dropdown');
        }
    }
);

export const fetchCategoriesDropdown = createAsyncThunk(
    'services/fetchCategoriesDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getCategoriesDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch categories dropdown');
        }
    }
);

export const fetchPartsList = createAsyncThunk(
    'services/fetchPartsList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartsList(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch parts list');
        }
    }
);

export const fetchVehicleDetail = createAsyncThunk(
    'services/fetchVehicleDetail',
    async ({ vehicleId, vehicleBrandId, ref_id }, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleDetail(vehicleId, vehicleBrandId, ref_id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle detail');
        }
    }
);

export const fetchVehicleSingleDetail = createAsyncThunk(
    'services/fetchVehicleSingleDetail',
    async (vehicleId, { rejectWithValue }) => {
        try {
            const response = await apiService.getVehicleSingleDetail(vehicleId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch vehicle single detail');
        }
    }
);

export const createServiceIssue = createAsyncThunk(
    'services/createServiceIssue',
    async (issueData, { rejectWithValue }) => {
        try {
            const response = await apiService.createServiceIssue(issueData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create service issue');
        }
    }
);

export const updateServiceIssue = createAsyncThunk(
    'services/updateServiceIssue',
    async (issueData, { rejectWithValue }) => {
        try {
            const response = await apiService.updateServiceIssue(issueData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update service issue');
        }
    }
);

export const createServiceEntry = createAsyncThunk(
    'services/createServiceEntry',
    async (serviceData, { rejectWithValue }) => {
        try {
            const response = await apiService.createServiceEntry(serviceData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create service entry');
        }
    }
);

export const updateServiceEntry = createAsyncThunk(
    'services/updateServiceEntry',
    async (serviceData, { rejectWithValue }) => {
        try {
            const response = await apiService.updateServiceEntry(serviceData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update service entry');
        }
    }
);

export const fetchNextServiceId = createAsyncThunk(
    'services/fetchNextServiceId',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getNextServiceId();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch next service id');
        }
    }
);

export const createServiceLineItem = createAsyncThunk(
    'services/createServiceLineItem',
    async (lineItemData, { rejectWithValue }) => {
        try {
            const response = await apiService.createServiceLineItem(lineItemData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create service line item');
        }
    }
);

export const updateServiceLineItem = createAsyncThunk(
    'services/updateServiceLineItem',
    async (lineItemData, { rejectWithValue }) => {
        try {
            const response = await apiService.updateServiceLineItem(lineItemData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update service line item');
        }
    }
);

export const deleteServiceLineItem = createAsyncThunk(
    'services/deleteServiceLineItem',
    async (serviceId, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteServiceLineItem(serviceId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete service line item');
        }
    }
);

export const updateServicePriorityApi = createAsyncThunk(
    'services/updateServicePriorityApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updateServicePriority(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update priority');
        }
    }
);

export const fetchServiceTagsDropdown = createAsyncThunk(
    'services/fetchServiceTagsDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getServiceTagsDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Tags dropdown');
        }
    }
);

export const updateServiceStatusApi = createAsyncThunk(
    'services/updateServiceStatusApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updateServiceStatus(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update service status');
        }
    }
);

const buildVehiclePaginatedMeta = (source = {}, request = {}) => {
    const totalItems =
        Number(source?.meta?.totalItems) ||
        Number(source?.total) ||
        (Array.isArray(source?.results) ? source.results.length : 0);
    const itemsPerPage =
        Number(source?.meta?.itemsPerPage) ||
        Number(request?.limit) ||
        10;
    const totalPages =
        Number(source?.meta?.totalPages) ||
        Math.max(1, Math.ceil(totalItems / Math.max(itemsPerPage, 1)));
    const currentPage =
        Number(source?.meta?.currentPage) ||
        Number(source?.page) ||
        Number(request?.page) ||
        1;

    return {
        ...(source?.meta || {}),
        totalItems,
        itemsPerPage,
        totalPages,
        currentPage,
    };
};

const initialState = {
    getServicesPaginated: [],
    getServicesLoading: false,
    getServicesError: null,

    serviceOverviewData: null,
    serviceOverviewLoading: false,
    serviceOverviewError: null,

    vehicleOverviewData: null,
    vehicleOverviewLoading: false,
    vehicleOverviewError: null,

    vehiclePaginatedData: [],
    vehiclePaginatedLoading: false,
    vehiclePaginatedError: null,
    deleteVehicleLoading: false,
    deleteVehicleError: null,
    vehicleMeta: {},
    vehicleHistoryData: [],
    vehicleHistoryLoading: false,
    vehicleHistoryError: null,
    vehicleServiceRecordsData: [],
    vehicleServiceRecordsLoading: false,
    vehicleServiceRecordsError: null,
    vehicleServiceRecordsMeta: {},
    vehicleTripsData: [],
    vehicleTripsLoading: false,
    vehicleTripsError: null,
    vehicleTripsMeta: {},
    vehicleFuelLogsData: [],
    vehicleFuelLogsLoading: false,
    vehicleFuelLogsError: null,
    vehicleFuelLogsMeta: {},
    vehicleDistanceSummaryData: null,
    vehicleDistanceSummaryLoading: false,
    vehicleDistanceSummaryError: null,
    vehicleInstallmentsData: [],
    vehicleInstallmentsLoading: false,
    vehicleInstallmentsError: null,
    vehicleAssignmentHistoryData: null,
    vehicleAssignmentHistoryLoading: false,
    vehicleAssignmentHistoryError: null,
    attachVehicleEquipmentLoading: false,
    attachVehicleEquipmentError: null,
    attachVehicleTyresLoading: false,
    attachVehicleTyresError: null,
    detachVehicleTyreLoading: false,
    detachVehicleTyreError: null,
    createVehicleInstallmentLoading: false,
    createVehicleInstallmentError: null,
    updateVehicleGeneralInfoData: null,
    updateVehicleGeneralInfoLoading: false,
    updateVehicleGeneralInfoError: null,
    updateVehiclePurchaseInfoData: null,
    updateVehiclePurchaseInfoLoading: false,
    updateVehiclePurchaseInfoError: null,
    commentOnVehicleData: null,
    commentOnVehicleLoading: false,
    commentOnVehicleError: null,

    vehicleData: [],
    vehicleDetail: null,
    loading: false,
    vehicleDetailLoading: false,
    vehicleSingleDetailData: null,
    vehicleSingleDetailLoading: false,
    vehicleSingleDetailError: null,
    createIssueLoading: false,
    createServiceLoading: false,
    createLineItemLoading: false,
    nextServiceId: null,
    nextServiceIdLoading: false,
    nextServiceIdError: null,

    categoriesData: [],
    categoriesLoading: false,

    partsData: [],
    partsLoading: false,

    getServicesReminderLoading: true,
    getServicesReminderPaginated: [],
    getServicesReminderError: null,

    singleServiceData: null,
    singleServiceLoading: false,
    singleServiceError: null,

    updateServiceEntryLoading: false,
    updateServiceData: null,
    updateServiceError: null,

    deleteLoading: false,

    servicePdfExportLoading: false,
    servicePdfExportData: {},
    servicePdfExportError: null,

    serviceExcelExportLoading: false,
    serviceExcelExportData: {},
    serviceExcelExportError: null,

    reasonsData: [],
    reasonsLoading: false,
    error: null,

    serviceUpdateData: null,
    serviceUpdateLoading: false,
    serviceUpdateError: null,

    // For updating priority
    servicePriorityUpdateLoading: false,
    servicePriorityUpdateError: null,

    // Tags Dropdown
    serviceTagsData: [],
    serviceTagsLoading: false,
    serviceTagsError: null,

    // For updating status
    serviceStatusUpdateLoading: false,
    serviceStatusUpdateError: null,
};

const serviceSlice = createSlice({
    name: 'services',
    initialState,
    reducers: {
        clearVehicleDetail: (state) => {
            state.vehicleDetail = null;
        },
        clearVehicleSingleDetailState: (state) => {
            state.vehicleSingleDetailData = null;
            state.vehicleSingleDetailLoading = false;
            state.vehicleSingleDetailError = null;
            state.updateVehicleGeneralInfoData = null;
            state.updateVehicleGeneralInfoLoading = false;
            state.updateVehicleGeneralInfoError = null;
            state.updateVehiclePurchaseInfoData = null;
            state.updateVehiclePurchaseInfoLoading = false;
            state.updateVehiclePurchaseInfoError = null;
        },
        clearVehicleHistoryState: (state) => {
            state.vehicleHistoryData = [];
            state.vehicleHistoryLoading = false;
            state.vehicleHistoryError = null;
            state.commentOnVehicleData = null;
            state.commentOnVehicleLoading = false;
            state.commentOnVehicleError = null;
        },
        clearVehicleServiceRecordsState: (state) => {
            state.vehicleServiceRecordsData = [];
            state.vehicleServiceRecordsLoading = false;
            state.vehicleServiceRecordsError = null;
            state.vehicleServiceRecordsMeta = {};
        },
        clearVehicleOperationalDataState: (state) => {
            state.vehicleTripsData = [];
            state.vehicleTripsLoading = false;
            state.vehicleTripsError = null;
            state.vehicleTripsMeta = {};
            state.vehicleFuelLogsData = [];
            state.vehicleFuelLogsLoading = false;
            state.vehicleFuelLogsError = null;
            state.vehicleFuelLogsMeta = {};
            state.vehicleDistanceSummaryData = null;
            state.vehicleDistanceSummaryLoading = false;
            state.vehicleDistanceSummaryError = null;
        },
        clearVehicleInstallmentsState: (state) => {
            state.vehicleInstallmentsData = [];
            state.vehicleInstallmentsLoading = false;
            state.vehicleInstallmentsError = null;
            state.vehicleAssignmentHistoryData = null;
            state.vehicleAssignmentHistoryLoading = false;
            state.vehicleAssignmentHistoryError = null;
            state.attachVehicleEquipmentLoading = false;
            state.attachVehicleEquipmentError = null;
            state.attachVehicleTyresLoading = false;
            state.attachVehicleTyresError = null;
            state.detachVehicleTyreLoading = false;
            state.detachVehicleTyreError = null;
            state.createVehicleInstallmentLoading = false;
            state.createVehicleInstallmentError = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(fetchServices.pending, (state) => {
                state.getServicesLoading = true;
                state.getServicesError = null;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.getServicesLoading = false;
                state.getServicesPaginated = action.payload?.data || [];
                state.getServicesError = null;
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.getServicesLoading = false;
                state.getServicesError = action.payload;
                state.getServicesPaginated = [];
            })

            .addCase(getServiceOverview.pending, (state) => {
                state.serviceOverviewLoading = true;
                state.serviceOverviewError = null;
            })
            .addCase(getServiceOverview.fulfilled, (state, action) => {
                state.serviceOverviewLoading = false;
                state.serviceOverviewData = action.payload?.data || null;
                state.serviceOverviewError = null;
            })
            .addCase(getServiceOverview.rejected, (state, action) => {
                state.serviceOverviewLoading = false;
                state.serviceOverviewError = action.payload;
                state.serviceOverviewData = null;
            })

            .addCase(fetchVehiclesOverview.pending, (state) => {
                state.vehicleOverviewLoading = true;
                state.vehicleOverviewError = null;
            })
            .addCase(fetchVehiclesOverview.fulfilled, (state, action) => {
                state.vehicleOverviewLoading = false;
                state.vehicleOverviewData = action.payload?.data || null;
                state.vehicleOverviewError = null;
            })
            .addCase(fetchVehiclesOverview.rejected, (state, action) => {
                state.vehicleOverviewLoading = false;
                state.vehicleOverviewError = action.payload;
                state.vehicleOverviewData = null;
            })

            .addCase(fetchVehicleData.pending, (state) => {
                state.vehiclePaginatedLoading = true;
                state.vehiclePaginatedError = null;
            })
            .addCase(fetchVehicleData.fulfilled, (state, action) => {
                const source = action.payload?.data;
                state.vehiclePaginatedLoading = false;
                state.vehiclePaginatedData = Array.isArray(source)
                    ? source
                    : source?.results || [];
                state.vehicleMeta =
                    source?.meta ||
                    action.payload?.meta ||
                    {};
                state.vehiclePaginatedError = null;
            })
            .addCase(fetchVehicleData.rejected, (state, action) => {
                state.vehiclePaginatedLoading = false;
                state.vehiclePaginatedData = [];
                state.vehicleMeta = {};
                state.vehiclePaginatedError = action.payload;
            })

            .addCase(deleteVehicle.pending, (state) => {
                state.deleteVehicleLoading = true;
                state.deleteVehicleError = null;
            })
            .addCase(deleteVehicle.fulfilled, (state, action) => {
                state.deleteVehicleLoading = false;
                state.deleteVehicleError = null;
                state.vehiclePaginatedData = state.vehiclePaginatedData.filter(
                    (row) => Number(row?.id) !== Number(action.payload?.deletedId)
                );
            })
            .addCase(deleteVehicle.rejected, (state, action) => {
                state.deleteVehicleLoading = false;
                state.deleteVehicleError = action.payload;
            })

            .addCase(fetchVehicleHistory.pending, (state) => {
                state.vehicleHistoryLoading = true;
                state.vehicleHistoryError = null;
            })
            .addCase(fetchVehicleHistory.fulfilled, (state, action) => {
                state.vehicleHistoryLoading = false;
                state.vehicleHistoryData = action.payload?.data || action.payload || [];
                state.vehicleHistoryError = null;
            })
            .addCase(fetchVehicleHistory.rejected, (state, action) => {
                state.vehicleHistoryLoading = false;
                state.vehicleHistoryData = [];
                state.vehicleHistoryError = action.payload;
            })

            .addCase(fetchVehicleServiceRecords.pending, (state) => {
                state.vehicleServiceRecordsLoading = true;
                state.vehicleServiceRecordsError = null;
            })
            .addCase(fetchVehicleServiceRecords.fulfilled, (state, action) => {
                const source = action.payload?.data || {};
                state.vehicleServiceRecordsLoading = false;
                state.vehicleServiceRecordsData = Array.isArray(source)
                    ? source
                    : source?.results || [];
                state.vehicleServiceRecordsMeta = buildVehiclePaginatedMeta(
                    source,
                    action.meta?.arg
                );
                state.vehicleServiceRecordsError = null;
            })
            .addCase(fetchVehicleServiceRecords.rejected, (state, action) => {
                state.vehicleServiceRecordsLoading = false;
                state.vehicleServiceRecordsData = [];
                state.vehicleServiceRecordsMeta = {};
                state.vehicleServiceRecordsError = action.payload;
            })

            .addCase(fetchVehicleTrips.pending, (state) => {
                state.vehicleTripsLoading = true;
                state.vehicleTripsError = null;
            })
            .addCase(fetchVehicleTrips.fulfilled, (state, action) => {
                const source = action.payload?.data || {};
                state.vehicleTripsLoading = false;
                state.vehicleTripsData = Array.isArray(source)
                    ? source
                    : source?.results || [];
                state.vehicleTripsMeta = buildVehiclePaginatedMeta(
                    source,
                    action.meta?.arg
                );
                state.vehicleTripsError = null;
            })
            .addCase(fetchVehicleTrips.rejected, (state, action) => {
                state.vehicleTripsLoading = false;
                state.vehicleTripsData = [];
                state.vehicleTripsMeta = {};
                state.vehicleTripsError = action.payload;
            })

            .addCase(fetchVehicleFuelLogs.pending, (state) => {
                state.vehicleFuelLogsLoading = true;
                state.vehicleFuelLogsError = null;
            })
            .addCase(fetchVehicleFuelLogs.fulfilled, (state, action) => {
                const source = action.payload?.data || {};
                state.vehicleFuelLogsLoading = false;
                state.vehicleFuelLogsData = Array.isArray(source)
                    ? source
                    : source?.results || [];
                state.vehicleFuelLogsMeta = buildVehiclePaginatedMeta(
                    source,
                    action.meta?.arg
                );
                state.vehicleFuelLogsError = null;
            })
            .addCase(fetchVehicleFuelLogs.rejected, (state, action) => {
                state.vehicleFuelLogsLoading = false;
                state.vehicleFuelLogsData = [];
                state.vehicleFuelLogsMeta = {};
                state.vehicleFuelLogsError = action.payload;
            })

            .addCase(fetchVehicleDistanceSummary.pending, (state) => {
                state.vehicleDistanceSummaryLoading = true;
                state.vehicleDistanceSummaryError = null;
            })
            .addCase(fetchVehicleDistanceSummary.fulfilled, (state, action) => {
                state.vehicleDistanceSummaryLoading = false;
                state.vehicleDistanceSummaryData = action.payload?.data || null;
                state.vehicleDistanceSummaryError = null;
            })
            .addCase(fetchVehicleDistanceSummary.rejected, (state, action) => {
                state.vehicleDistanceSummaryLoading = false;
                state.vehicleDistanceSummaryData = null;
                state.vehicleDistanceSummaryError = action.payload;
            })

            .addCase(fetchVehicleInstallments.pending, (state) => {
                state.vehicleInstallmentsLoading = true;
                state.vehicleInstallmentsError = null;
            })
            .addCase(fetchVehicleInstallments.fulfilled, (state, action) => {
                const source = action.payload?.data;
                state.vehicleInstallmentsLoading = false;
                state.vehicleInstallmentsData = Array.isArray(source)
                    ? source
                    : Array.isArray(source?.results)
                        ? source.results
                        : [];
                state.vehicleInstallmentsError = null;
            })
            .addCase(fetchVehicleInstallments.rejected, (state, action) => {
                state.vehicleInstallmentsLoading = false;
                state.vehicleInstallmentsData = [];
                state.vehicleInstallmentsError = action.payload;
            })

            .addCase(fetchVehicleAssignmentHistory.pending, (state) => {
                state.vehicleAssignmentHistoryLoading = true;
                state.vehicleAssignmentHistoryError = null;
            })
            .addCase(fetchVehicleAssignmentHistory.fulfilled, (state, action) => {
                state.vehicleAssignmentHistoryLoading = false;
                state.vehicleAssignmentHistoryData = action.payload?.data || null;
                state.vehicleAssignmentHistoryError = null;
            })
            .addCase(fetchVehicleAssignmentHistory.rejected, (state, action) => {
                state.vehicleAssignmentHistoryLoading = false;
                state.vehicleAssignmentHistoryData = null;
                state.vehicleAssignmentHistoryError = action.payload;
            })

            .addCase(attachVehicleEquipment.pending, (state) => {
                state.attachVehicleEquipmentLoading = true;
                state.attachVehicleEquipmentError = null;
            })
            .addCase(attachVehicleEquipment.fulfilled, (state) => {
                state.attachVehicleEquipmentLoading = false;
                state.attachVehicleEquipmentError = null;
            })
            .addCase(attachVehicleEquipment.rejected, (state, action) => {
                state.attachVehicleEquipmentLoading = false;
                state.attachVehicleEquipmentError = action.payload;
            })
            .addCase(detachVehicleEquipment.pending, (state) => {
                state.attachVehicleEquipmentLoading = true;
                state.attachVehicleEquipmentError = null;
            })
            .addCase(detachVehicleEquipment.fulfilled, (state) => {
                state.attachVehicleEquipmentLoading = false;
                state.attachVehicleEquipmentError = null;
            })
            .addCase(detachVehicleEquipment.rejected, (state, action) => {
                state.attachVehicleEquipmentLoading = false;
                state.attachVehicleEquipmentError = action.payload;
            })
            .addCase(attachVehicleTyres.pending, (state) => {
                state.attachVehicleTyresLoading = true;
                state.attachVehicleTyresError = null;
            })
            .addCase(attachVehicleTyres.fulfilled, (state) => {
                state.attachVehicleTyresLoading = false;
                state.attachVehicleTyresError = null;
            })
            .addCase(attachVehicleTyres.rejected, (state, action) => {
                state.attachVehicleTyresLoading = false;
                state.attachVehicleTyresError = action.payload;
            })
            .addCase(detachVehicleTyre.pending, (state) => {
                state.detachVehicleTyreLoading = true;
                state.detachVehicleTyreError = null;
            })
            .addCase(detachVehicleTyre.fulfilled, (state) => {
                state.detachVehicleTyreLoading = false;
                state.detachVehicleTyreError = null;
            })
            .addCase(detachVehicleTyre.rejected, (state, action) => {
                state.detachVehicleTyreLoading = false;
                state.detachVehicleTyreError = action.payload;
            })

            .addCase(createVehicleInstallment.pending, (state) => {
                state.createVehicleInstallmentLoading = true;
                state.createVehicleInstallmentError = null;
            })
            .addCase(createVehicleInstallment.fulfilled, (state) => {
                state.createVehicleInstallmentLoading = false;
                state.createVehicleInstallmentError = null;
            })
            .addCase(createVehicleInstallment.rejected, (state, action) => {
                state.createVehicleInstallmentLoading = false;
                state.createVehicleInstallmentError = action.payload;
            })

            .addCase(updateVehicleGeneralInfo.pending, (state) => {
                state.updateVehicleGeneralInfoLoading = true;
                state.updateVehicleGeneralInfoError = null;
            })
            .addCase(updateVehicleGeneralInfo.fulfilled, (state, action) => {
                state.updateVehicleGeneralInfoLoading = false;
                state.updateVehicleGeneralInfoData = action.payload?.data || action.payload || null;
                state.updateVehicleGeneralInfoError = null;
            })
            .addCase(updateVehicleGeneralInfo.rejected, (state, action) => {
                state.updateVehicleGeneralInfoLoading = false;
                state.updateVehicleGeneralInfoError = action.payload;
            })

            .addCase(updateVehiclePurchaseInfo.pending, (state) => {
                state.updateVehiclePurchaseInfoLoading = true;
                state.updateVehiclePurchaseInfoError = null;
            })
            .addCase(updateVehiclePurchaseInfo.fulfilled, (state, action) => {
                state.updateVehiclePurchaseInfoLoading = false;
                state.updateVehiclePurchaseInfoData = action.payload?.data || action.payload || null;
                state.updateVehiclePurchaseInfoError = null;
            })
            .addCase(updateVehiclePurchaseInfo.rejected, (state, action) => {
                state.updateVehiclePurchaseInfoLoading = false;
                state.updateVehiclePurchaseInfoError = action.payload;
            })

            .addCase(commentOnVehicle.pending, (state) => {
                state.commentOnVehicleLoading = true;
                state.commentOnVehicleError = null;
            })
            .addCase(commentOnVehicle.fulfilled, (state, action) => {
                state.commentOnVehicleLoading = false;
                state.commentOnVehicleData = action.payload?.data || action.payload || null;
                state.commentOnVehicleError = null;
            })
            .addCase(commentOnVehicle.rejected, (state, action) => {
                state.commentOnVehicleLoading = false;
                state.commentOnVehicleData = null;
                state.commentOnVehicleError = action.payload;
            })

            .addCase(fetchServicesReminder.pending, (state) => {
                state.getServicesReminderLoading = true;
                state.getServicesReminderError = null;
            })
            .addCase(fetchServicesReminder.fulfilled, (state, action) => {
                state.getServicesReminderLoading = false;
                state.getServicesReminderPaginated = action.payload?.data || [];
                state.getServicesReminderError = null;
            })
            .addCase(fetchServicesReminder.rejected, (state, action) => {
                state.getServicesReminderLoading = false;
                state.getServicesReminderError = action.payload;
                state.getServicesReminderPaginated = [];
            })

            .addCase(getSingleService.pending, (state) => {
                state.singleServiceLoading = true;
                state.singleServiceError = null;
            })
            .addCase(getSingleService.fulfilled, (state, action) => {
                state.singleServiceLoading = false;
                state.singleServiceData = action.payload?.data || null;
                state.singleServiceError = null;
            })
            .addCase(getSingleService.rejected, (state, action) => {
                state.singleServiceLoading = false;
                state.singleServiceError = action.payload;
                state.singleServiceData = null;
            })

            .addCase(deleteService.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteService.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.error = null;
            })
            .addCase(deleteService.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchVehicleDropdown.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVehicleDropdown.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicleData = action.payload?.data || [];
                state.error = null;
            })
            .addCase(fetchVehicleDropdown.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.vehicleData = [];
            })

            .addCase(fetchPartsList.pending, (state) => {
                state.partsLoading = true;
                state.error = null;
            })
            .addCase(fetchPartsList.fulfilled, (state, action) => {
                state.partsLoading = false;
                const partsSource = action.payload?.data ?? action.payload;
                state.partsData = Array.isArray(partsSource)
                    ? partsSource
                    : Array.isArray(partsSource?.results)
                        ? partsSource.results
                        : [];
                state.error = null;
            })
            .addCase(fetchPartsList.rejected, (state, action) => {
                state.partsLoading = false;
                state.error = action.payload;
                state.partsData = [];
            })

            .addCase(fetchReasonsDropdown.pending, (state) => {
                state.reasonsLoading = true;
            })
            .addCase(fetchReasonsDropdown.fulfilled, (state, action) => {
                state.reasonsLoading = false;
                state.reasonsData = action.payload?.data || [];
            })
            .addCase(fetchCategoriesDropdown.pending, (state) => {
                state.categoriesLoading = true;
            })
            .addCase(fetchCategoriesDropdown.fulfilled, (state, action) => {
                state.categoriesLoading = false;
                state.categoriesData = action.payload?.data || [];
            })
            .addCase(fetchVehicleDetail.pending, (state) => {
                state.vehicleDetailLoading = true;
                state.error = null;
            })
            .addCase(fetchVehicleDetail.fulfilled, (state, action) => {
                state.vehicleDetailLoading = false;
                state.vehicleDetail = action.payload?.data || null;
                state.error = null;
            })
            .addCase(fetchVehicleDetail.rejected, (state, action) => {
                state.vehicleDetailLoading = false;
                state.error = action.payload;
                state.vehicleDetail = null;
            })
            .addCase(fetchVehicleSingleDetail.pending, (state) => {
                state.vehicleSingleDetailLoading = true;
                state.vehicleSingleDetailError = null;
            })
            .addCase(fetchVehicleSingleDetail.fulfilled, (state, action) => {
                state.vehicleSingleDetailLoading = false;
                state.vehicleSingleDetailData = action.payload?.data || null;
                state.vehicleSingleDetailError = null;
            })
            .addCase(fetchVehicleSingleDetail.rejected, (state, action) => {
                state.vehicleSingleDetailLoading = false;
                state.vehicleSingleDetailData = null;
                state.vehicleSingleDetailError = action.payload;
            })
            .addCase(createServiceIssue.pending, (state) => {
                state.createIssueLoading = true;
                state.error = null;
            })
            .addCase(createServiceIssue.fulfilled, (state, action) => {
                state.createIssueLoading = false;
                state.error = null;
            })
            .addCase(createServiceIssue.rejected, (state, action) => {
                state.createIssueLoading = false;
                state.error = action.payload;
            })
            .addCase(createServiceEntry.pending, (state) => {
                state.createServiceLoading = true;
                state.error = null;
            })
            .addCase(createServiceEntry.fulfilled, (state, action) => {
                state.createServiceLoading = false;
                state.error = null;
            })
            .addCase(createServiceEntry.rejected, (state, action) => {
                state.createServiceLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchNextServiceId.pending, (state) => {
                state.nextServiceIdLoading = true;
                state.nextServiceIdError = null;
            })
            .addCase(fetchNextServiceId.fulfilled, (state, action) => {
                state.nextServiceIdLoading = false;
                state.nextServiceId = action.payload?.data ?? null;
                state.nextServiceIdError = null;
            })
            .addCase(fetchNextServiceId.rejected, (state, action) => {
                state.nextServiceIdLoading = false;
                state.nextServiceIdError = action.payload;
                state.nextServiceId = null;
            })

            .addCase(updateServiceEntry.pending, (state) => {
                state.updateServiceEntryLoading = true;
                state.updateServiceError = null;
            })
            .addCase(updateServiceEntry.fulfilled, (state, action) => {
                state.updateServiceEntryLoading = false;
                state.updateServiceError = null;
            })
            .addCase(updateServiceEntry.rejected, (state, action) => {
                state.updateServiceEntryLoading = false;
                state.updateServiceError = action.payload;
            })

            .addCase(createServiceLineItem.pending, (state) => {
                state.createLineItemLoading = true;
                state.error = null;
            })
            .addCase(createServiceLineItem.fulfilled, (state, action) => {
                state.createLineItemLoading = false;
                state.error = null;
            })
            .addCase(createServiceLineItem.rejected, (state, action) => {
                state.createLineItemLoading = false;
                state.error = action.payload;
            })

            .addCase(servicePdfExport.pending, (state) => {
                state.servicePdfExportLoading = true;
                state.servicePdfExportError = null;
            })
            .addCase(servicePdfExport.fulfilled, (state, action) => {
                state.servicePdfExportLoading = false;
                state.servicePdfExportData = action.payload?.data || {};
                state.servicePdfExportError = null;
            })
            .addCase(servicePdfExport.rejected, (state, action) => {
                state.servicePdfExportLoading = false;
                state.servicePdfExportError = action.payload;
            })

            // Excel Export
            .addCase(serviceExcelExport.pending, (state) => {
                state.serviceExcelExportLoading = true;
                state.serviceExcelExportError = null;
            })
            .addCase(serviceExcelExport.fulfilled, (state, action) => {
                state.serviceExcelExportLoading = false;
                state.serviceExcelExportData = action.payload?.data || {};
                state.serviceExcelExportError = null;
            })
            .addCase(serviceExcelExport.rejected, (state, action) => {
                state.serviceExcelExportLoading = false;
                state.serviceExcelExportError = action.payload;
            })

            .addCase(updateServiceIssue.pending, (state) => {
                state.serviceUpdateLoading = true;
                state.serviceUpdateError = null;
            })
            .addCase(updateServiceIssue.fulfilled, (state, action) => {
                state.serviceUpdateLoading = false;
                state.serviceUpdateData = action.payload?.data || null;
                state.serviceUpdateError = null;
            })
            .addCase(updateServiceIssue.rejected, (state, action) => {
                state.serviceUpdateLoading = false;
                state.serviceUpdateError = action.payload;
                state.serviceUpdateData = null;
            })

            .addCase(deleteServiceLineItem.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteServiceLineItem.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.error = null;
            })
            .addCase(deleteServiceLineItem.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })

            // Update Service Priority
            .addCase(updateServicePriorityApi.pending, (state) => {
                state.servicePriorityUpdateLoading = true;
                state.servicePriorityUpdateError = null;
            })
            .addCase(updateServicePriorityApi.fulfilled, (state, action) => {
                state.servicePriorityUpdateLoading = false;
                state.servicePriorityUpdateError = null;
            })
            .addCase(updateServicePriorityApi.rejected, (state, action) => {
                state.servicePriorityUpdateLoading = false;
                state.servicePriorityUpdateError = action.payload;
            })

            // Fetch Service Tags Dropdown
            .addCase(fetchServiceTagsDropdown.pending, (state) => {
                state.serviceTagsLoading = true;
                state.serviceTagsError = null;
            })
            .addCase(fetchServiceTagsDropdown.fulfilled, (state, action) => {
                state.serviceTagsLoading = false;
                state.serviceTagsData = action.payload?.data || [];
                state.serviceTagsError = null;
            })
            .addCase(fetchServiceTagsDropdown.rejected, (state, action) => {
                state.serviceTagsLoading = false;
                state.serviceTagsError = action.payload;
            })

            // Fetch Service Status Dropdown
            .addCase(updateServiceStatusApi.pending, (state) => {
                state.serviceStatusUpdateLoading = true;
                state.serviceStatusUpdateError = null;
            })
            .addCase(updateServiceStatusApi.fulfilled, (state, action) => {
                state.serviceStatusUpdateLoading = false;
                state.serviceStatusUpdateError = null;
            })
            .addCase(updateServiceStatusApi.rejected, (state, action) => {
                state.serviceStatusUpdateLoading = false;
                state.serviceStatusUpdateError = action.payload;
            })
    }
});

export const {
    clearVehicleDetail,
    clearVehicleSingleDetailState,
    clearVehicleHistoryState,
    clearVehicleServiceRecordsState,
    clearVehicleOperationalDataState,
    clearVehicleInstallmentsState,
    clearError,
} = serviceSlice.actions;

export default serviceSlice.reducer;
